import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

export interface SearchablePickerOption {
  id: number;
  label: string;
  subLabel?: string;
}

@Component({
  selector: 'app-pss-searchable-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pss-searchable-picker.component.html',
  styleUrl: './pss-searchable-picker.component.css',
})
export class PssSearchablePickerComponent implements OnChanges {
  @Input() label = '';
  @Input() icon = 'search';
  @Input() placeholder = 'ค้นหาและเลือก...';
  @Input() required = false;
  @Input() disabled = false;
  @Input() selectedId: number | null = null;
  @Input() displayText = '';
  @Input() options: SearchablePickerOption[] = [];
  @Input() total = 0;
  @Input() previewSize = 20;
  @Input() loading = false;

  @Output() opened = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() optionSelected = new EventEmitter<SearchablePickerOption>();
  @Output() cleared = new EventEmitter<void>();

  searchText = '';
  isOpen = false;
  private hasLoadedOnce = false;

  get hintText(): string {
    if (this.loading) return 'กำลังโหลด...';
    if (!this.hasLoadedOnce && this.options.length === 0) {
      return 'คลิกเพื่อเลือกรายการ';
    }
    if (this.total <= this.previewSize) {
      return this.total > 0 ? `ทั้งหมด ${this.total} รายการ` : 'ไม่พบรายการ';
    }
    return `แสดง ${Math.min(this.options.length, this.previewSize)} จาก ${this.total} — พิมพ์ค้นหาเพื่อกรอง`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['displayText'] && !this.isOpen) {
      this.searchText = this.displayText || '';
    }
    if (changes['loading'] && !this.loading) {
      this.hasLoadedOnce = true;
    }
    if (changes['options'] || changes['total']) {
      if (this.options.length > 0 || this.total > 0) {
        this.hasLoadedOnce = true;
      }
    }
  }

  onFocus(): void {
    if (this.disabled) return;
    this.isOpen = true;
    this.searchText = this.displayText || '';
    this.opened.emit();
    // Load preview list unfiltered; displayText stays in the input until user types
    this.searchChange.emit('');
  }

  onInput(): void {
    if (this.disabled) return;
    this.isOpen = true;
    this.searchChange.emit(this.searchText);
  }

  onBlur(): void {
    setTimeout(() => {
      this.isOpen = false;
      this.searchText = this.displayText || '';
    }, 150);
  }

  selectOption(option: SearchablePickerOption): void {
    this.optionSelected.emit(option);
    this.searchText = option.label;
    this.isOpen = false;
  }

  clearSelection(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchText = '';
    this.cleared.emit();
  }
}
